import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { ReactNode, useEffect } from 'react';
import { I18nContextProvider, useCheckAuth } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { themeCommon } from '@sh4res/bo-common/theme';

import { i18nProvider } from '@Utils/providers/i18nProvider';

const LoginPageOverlay = styled(Box)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
`;

const LoginPageContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  position: absolute;
  min-width: 400px;
  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(89, 79, 244, 0.04);
  border-radius: 30px;
  position: relative;
`;

const theme = createTheme(themeCommon);

export function AuthPage({
  allowLoggedIn = false,
  children,
}: {
  children: ReactNode;
  allowLoggedIn?: boolean;
}) {
  const checkAuth = useCheckAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (allowLoggedIn) {
      return;
    }

    checkAuth({}, false)
      // eslint-disable-next-line promise/prefer-await-to-then, promise/always-return -- sync API
      .then(() => {
        // already authenticated, redirect to the home page
        navigate('/');
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- sync API
      .catch(() => {
        // not authenticated, stay on the login page
      });
  }, [checkAuth, navigate, allowLoggedIn]);

  return (
    <ThemeProvider theme={theme}>
      <I18nContextProvider value={i18nProvider}>
        <>
          <GlobalStyles
            styles={{
              body: {
                margin: 0,
                fontFamily:
                  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                webkitFontSmoothing: 'antialiased',
                mozOsxFontSmoothing: 'grayscale',
              },
              code: {
                fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
              },
              '@media only screen': {
                html: {
                  overflow: 'hidden',
                  height: '100%',
                },
                body: {
                  height: '100%',
                  overflow: 'auto',
                },
              },
            }}
          />
          <LoginPageOverlay>
            <Box
              sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoginPageContent>{children}</LoginPageContent>
            </Box>
          </LoginPageOverlay>
        </>
      </I18nContextProvider>
    </ThemeProvider>
  );
}
